import _normalizeInlineWhitespace from "../quill/normalizeInlineWhitespace.js";
import _formattedWords from "../quill/formattedWords.js";
import _isBrOnlyLine from "../quill/isBrOnlyLine.js";
import _balanceTextOld from "../utility/balanceTextOld.js";
import _balanceText from "../utility/balanceText.js";
import _closeHtmlTags from "../quill/closeHtmlTags.js";
import _convertToPlainText from "../quill/convertToPlainText.js";
import _convertToHtml from "../quill/convertToHtml.js";
import _Event from "../../classes/event.js";
const Event = _Event;
const convertToHtml = _convertToHtml;
const convertToPlainText = _convertToPlainText;
const closeHtmlTags = _closeHtmlTags;
const balanceText = _balanceText;
const balanceTextOld = _balanceTextOld;
const isBrOnlyLine = _isBrOnlyLine;
const formattedWords = _formattedWords;
const normalizeInlineWhitespace = _normalizeInlineWhitespace;
export default (function autoFormat(eventGroup, maxLines, maxChars, minDuration, allowOrphanWords, selectedEvents = null) {
  maxLines = Math.max(1, maxLines || 1);
  maxChars = Math.max(1, maxChars || 1);
  minDuration = Number(minDuration);
  if (!Number.isFinite(minDuration) || minDuration < 0) {
    minDuration = 0;
  }
  let events = [];
  eventGroup.events.forEach((ccEvent, count) => {
    // console.log("-----");
    if (!selectedEvents || selectedEvents.indexOf(count) > -1) {
      // console.log(`Working on event ${count}`);
      ccEvent.text = stripBrOnlyParagraphs(ccEvent.text);
      let textLines;
      let plainText = convertToPlainText(ccEvent.text, " ");
      let useSpeakerBalance = hasSpeakerTag(plainText);

      // When multi-event output is needed, prefer original line breaks that
      // already fit maxChars and only rebalance overflowing lines.
      let hybridLines = buildHybridLines(ccEvent.text, maxChars, useSpeakerBalance);
      if (hybridLines && hybridLines.length > maxLines) {
        textLines = hybridLines;
      } else {
        // Calculate how many lines are needed to fit all text under maxChars.
        // Pass this to the balance functions so they can produce enough lines,
        // then autoFormat groups those lines into events of maxLines each.
        let estimatedLines = Math.max(maxLines, Math.ceil(plainText.length / maxChars));
        if (useSpeakerBalance) {
          textLines = balanceKeepingFormat(balanceText, ccEvent.text, maxChars, estimatedLines);
        } else {
          textLines = balanceKeepingFormat(balanceTextOld, ccEvent.text, maxChars, estimatedLines);
        }
      }
      textLines = filterBrOnlyLines(textLines);

      // console.log(textLines);
      let numberOfEvents = Math.ceil(textLines.length / maxLines);
      // console.log(`There are ${numberOfEvents} events required for formatting of text ${textLines}`);
      let avgDuration = Math.max(minDuration, (ccEvent.end - ccEvent.start) / numberOfEvents);
      ccEvent.text = textLines.splice(0, maxLines).join("\n");
      if (!allowOrphanWords && canMergeTrailingOrphanWord(ccEvent.text, textLines[0], maxChars)) {
        ccEvent.text += " " + textLines[0];
        textLines = [];
        numberOfEvents = 1;
      }
      ccEvent.text = closeHtmlTags(ccEvent.text);
      ccEvent.text = normalizeInlineWhitespace(convertToHtml(ccEvent.text));
      if (numberOfEvents > 1) {
        ccEvent.end = ccEvent.start + avgDuration;
        events.push(ccEvent);
        let safetyLimit = 1000;
        while (textLines.length > 0 && safetyLimit > 0) {
          safetyLimit--;
          let t = textLines.splice(0, maxLines).join("\n");
          if (!allowOrphanWords && canMergeTrailingOrphanWord(t, textLines[0], maxChars)) {
            t += " " + textLines[0];
            textLines = [];
          }
          events.push(new Event({
            ...ccEvent,
            text: normalizeInlineWhitespace(convertToHtml(closeHtmlTags(t))),
            start: events[events.length - 1].end,
            end: events[events.length - 1].end + avgDuration,
            original: ""
          }));
        }
      } else {
        events.push(ccEvent);
      }
    } else {
      events.push(ccEvent);
    }
  });
  eventGroup.events = events;
  return eventGroup;
});
/*
    Balance lines on plain words, then put each word's formatting back.
    Balancing raw HTML splits tags apart and closes formatting per line, which drops
    italics/bold/colour on wrapped lines. Falls back to the raw HTML when the balancer
    changes the words (e.g. character-based splitting for CJK text).
*/
function balanceKeepingFormat(balanceFn, html, maxChars, lines) {
  let tokens = formattedWords.tokenize(html);
  if (tokens) {
    let balanced = balanceFn(formattedWords.toPlainHtml(tokens), maxChars, lines);
    let rebuilt = formattedWords.rebuildLines(balanced, tokens);
    if (rebuilt) {
      return rebuilt;
    }
  }
  return balanceFn(html, maxChars, lines);
}
function stripBrOnlyParagraphs(html) {
  return (html || "").replace(/<p\b[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, "");
}
function filterBrOnlyLines(lines) {
  return lines.filter(line => !isBrOnlyLine(line));
}
function extractOriginalHtmlLines(html) {
  if (!html) {
    return [];
  }
  let lines = [];
  let regex = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    let inner = (match[1] || "").trim();
    if (inner && !isBrOnlyLine(inner)) {
      lines.push(inner);
    }
  }
  return lines;
}
function buildHybridLines(html, maxChars, useSpeakerBalance) {
  let originals = extractOriginalHtmlLines(html);
  if (originals.length === 0) {
    return null;
  }
  let balanceFn = useSpeakerBalance ? balanceText : balanceTextOld;
  let hybridLines = [];
  originals.forEach(line => {
    let plainLen = convertToPlainText(line, " ").length;
    if (plainLen <= maxChars) {
      hybridLines.push(line);
    } else {
      let estimatedLines = Math.max(1, Math.ceil(plainLen / maxChars));
      let balanced = balanceKeepingFormat(balanceFn, `<p>${line}</p>`, maxChars, estimatedLines);
      hybridLines.push(...filterBrOnlyLines(balanced));
    }
  });
  return hybridLines;
}
function hasSpeakerTag(text) {
  // Match dash-style speaker tags (-, —, >) at the start of text or after whitespace
  // Match bracket-style speaker IDs ([Name], (Name), {Name}, <Name>) at start of text or after whitespace
  const pattern = /(?:^|\s)[-—>]|(?:^|\s)[\[({<][^\])}>\n]+[\])}>]/;
  return pattern.test(text);
}
function canMergeTrailingOrphanWord(text, orphanLine, maxChars) {
  if (!orphanLine || orphanLine.trim().split(/\s+/).length !== 1) {
    return false;
  }
  let lines = text.split("\n");
  let lastLine = lines[lines.length - 1] || "";
  let mergedLine = lastLine ? lastLine + " " + orphanLine : orphanLine;
  return convertToPlainText(mergedLine, " ").length <= maxChars;
}